import { useState } from "react";
import { useAuth } from "../lib/auth";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { MEDICAL_SPECIALTIES } from "../lib/specialties";
import { AuthCard, PrimaryButton } from "./Login";

/**
 * SelectSpecialty — estilo minimalista "Files Management".
 * Fondo blanco full-screen. Paleta blanco / gris #F2F3F5 / negro.
 *
 * Pantalla obligatoria para doctores registrados que aún no tienen
 * `specialty`. Bloquea la navegación al resto del sistema hasta que
 * la guarden.
 */
export default function SelectSpecialty() {
  const { user, setUser } = useAuth();
  const [specialty, setSpecialty] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!specialty) {
      toast.error("Selecciona una especialidad para continuar");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { specialty });
      setUser(data);
      toast.success("Especialidad guardada");
      window.location.replace("/doctor");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
      <AuthCard>
        <div className="flex justify-center mb-8">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="h-12 w-12 object-contain"
          />
        </div>

        <h1 className="text-center text-3xl sm:text-[32px] font-semibold text-black tracking-tight leading-tight">
          Un último detalle
        </h1>
        <p className="text-center text-sm text-black/50 mt-2 leading-relaxed">
          Hola, <span className="font-medium text-black">{user?.name}</span>.
          Selecciona tu especialidad médica — aparecerá en tus recetas y en tu
          perfil de doctor.
        </p>

        <form onSubmit={save} className="mt-8 space-y-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
              Especialidad
            </span>
            <div className="relative">
              <select
                required
                data-testid="select-specialty"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                className={`w-full appearance-none bg-[#F2F3F5] hover:bg-[#EDEEF0] focus:bg-white border border-transparent focus:border-black/80 rounded-xl px-4 py-3.5 text-[15px] font-medium outline-none transition-all pr-10 ${
                  specialty ? "text-black" : "text-black/30"
                }`}
              >
                <option value="" disabled>
                  Selecciona tu especialidad
                </option>
                {MEDICAL_SPECIALTIES.map((opt) => (
                  <option key={opt} value={opt} className="text-black">
                    {opt}
                  </option>
                ))}
              </select>
              <svg
                className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </label>

          <PrimaryButton
            type="submit"
            loading={saving}
            testId="select-specialty-submit"
          >
            Continuar
          </PrimaryButton>

          <p className="text-[11px] text-black/45 text-center mt-3">
            Podrás cambiarla en cualquier momento desde tu perfil.
          </p>
        </form>
      </AuthCard>
    </div>
  );
}
